import {
  getAllValidations,
  getValidation,
  type ValidationMeta,
  type ValidationResult,
} from './validation-registry';
import {
  type CountryValidationContext,
  type FieldValidationAssignment,
} from '@xanectech/types';
import { createLogger } from '../logger';

const logger = createLogger('Lib - country-validation-manager');

/**
 * Country Validation Context Manager for Field Validation
 * Handles assignment of TypeScript validation functions to fields with country contexts
 * All validation functions are created by the PIIKeep development team in TypeScript files
 */

export interface ValidationRule {
  functionName: string;
  contextName: string;
  priority: number;
  appliesWhenCountries: string[];
  excludesCountries: string[];
  appliesWhenDocumentTypes: string[];
  excludesDocumentTypes: string[];
  config: Record<string, string | number | boolean>;
  errorMessageOverride?: string;
}

/**
 * Enhanced validation manager for country-context field validation
 * Works with TypeScript validation registry - no database function storage
 */
export class CountryValidationManager {
  private fieldAssignments: Map<string, FieldValidationAssignment[]> =
    new Map();

  /**
   * Get available validation functions from TypeScript registry
   */
  public getAvailableValidations(): Array<{
    name: string;
    meta: ValidationMeta;
  }> {
    const validations: Array<{ name: string; meta: ValidationMeta }> = [];
    const allValidations = getAllValidations();

    for (const [name, registeredValidation] of allValidations) {
      validations.push({ name, meta: registeredValidation.meta });
    }

    return validations;
  }

  /**
   * Get validation functions for a specific country
   */
  public getValidationsForCountry(
    countryCode: string,
  ): Array<{ name: string; meta: ValidationMeta }> {
    return this.getAvailableValidations().filter(
      ({ meta }) => meta.country === countryCode || !meta.country,
    );
  }

  /**
   * Assign validation functions to a field with country context
   */
  public assignValidationToField(assignment: FieldValidationAssignment): void {
    const fieldAssignments =
      this.fieldAssignments.get(assignment.fieldId) || [];

    // Check if assignment already exists
    const existingIndex = fieldAssignments.findIndex(
      (a) =>
        a.validationFunctionName === assignment.validationFunctionName &&
        a.contextName === assignment.contextName,
    );

    if (existingIndex >= 0) {
      // Update existing assignment
      fieldAssignments[existingIndex] = assignment;
    } else {
      // Add new assignment
      fieldAssignments.push(assignment);
    }

    this.fieldAssignments.set(assignment.fieldId, fieldAssignments);
  }

  /**
   * Get validation rules for a field based on country context
   */
  public getFieldValidationRules(
    fieldId: string,
    countryContext: Record<string, string> = {},
  ): ValidationRule[] {
    const assignments = this.fieldAssignments.get(fieldId) || [];

    return assignments
      .filter((assignment) =>
        this.shouldApplyValidation(assignment, countryContext),
      )
      .map((assignment) => ({
        functionName: assignment.validationFunctionName,
        contextName: assignment.contextName,
        priority: assignment.priority || 0,
        appliesWhenCountries: assignment.appliesWhenCountries || [],
        excludesCountries: assignment.excludesCountries || [],
        appliesWhenDocumentTypes: assignment.appliesWhenDocumentTypes || [],
        excludesDocumentTypes: assignment.excludesDocumentTypes || [],
        config: assignment.validationConfig || {},
        errorMessageOverride: assignment.errorMessageOverride,
      }))
      .sort((a, b) => b.priority - a.priority); // Sort by priority descending
  }

  /**
   * Validate a field value with country context
   */
  public validateFieldWithContext(
    fieldId: string,
    value: string,
    countryContext: Record<string, string> = {},
  ): { isValid: boolean; message: string; appliedRules: string[] } {
    const rules = this.getFieldValidationRules(fieldId, countryContext);
    const appliedRules: string[] = [];

    for (const rule of rules) {
      const validation = getValidation(rule.functionName);

      if (!validation) {
        logger.warn(
          `Validation function '${rule.functionName}' not found in registry`,
        );
        continue;
      }

      appliedRules.push(rule.functionName);

      try {
        const result = validation.validate(value);

        if (!result.isValid) {
          return {
            isValid: false,
            message:
              rule.errorMessageOverride ||
              result.message ||
              'Validation failed',
            appliedRules,
          };
        }
      } catch (error) {
        return {
          isValid: false,
          message: `Validation error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          appliedRules,
        };
      }
    }

    return {
      isValid: true,
      message: 'All validations passed',
      appliedRules,
    };
  }

  /**
   * Test validation function with provided test cases
   */
  public testValidationFunction(
    functionName: string,
    testCases: Array<{
      input: string;
      expectedValid: boolean;
      description?: string;
    }>,
  ): Array<{
    input: string;
    expected: boolean;
    actual: boolean;
    passed: boolean;
    description?: string;
    result?: ValidationResult;
  }> {
    const validation = getValidation(functionName);

    if (!validation) {
      throw new Error(`Validation function '${functionName}' not found`);
    }

    return testCases.map((testCase) => {
      try {
        const result = validation.validate(testCase.input);
        const actual = result.isValid;
        const passed = actual === testCase.expectedValid;

        return {
          input: testCase.input,
          expected: testCase.expectedValid,
          actual,
          passed,
          description: testCase.description,
          result,
        };
      } catch (error) {
        return {
          input: testCase.input,
          expected: testCase.expectedValid,
          actual: false,
          passed: false,
          description: testCase.description,
          result: {
            isValid: false,
            message:
              error instanceof Error ? error.message : 'Test execution error',
          },
        };
      }
    });
  }

  /**
   * Remove validation assignment from field
   */
  public removeValidationFromField(
    fieldId: string,
    validationFunctionName: string,
    contextName: string,
  ): boolean {
    const assignments = this.fieldAssignments.get(fieldId);
    if (!assignments) return false;

    const filteredAssignments = assignments.filter(
      (a) =>
        !(
          a.validationFunctionName === validationFunctionName &&
          a.contextName === contextName
        ),
    );

    if (filteredAssignments.length === assignments.length) return false;

    if (filteredAssignments.length === 0) {
      this.fieldAssignments.delete(fieldId);
    } else {
      this.fieldAssignments.set(fieldId, filteredAssignments);
    }

    return true;
  }

  /**
   * Check if validation should be applied based on country context
   */
  private shouldApplyValidation(
    assignment: FieldValidationAssignment,
    countryContext: Record<string, string>,
  ): boolean {
    // If no country restrictions, always apply
    if (
      (!assignment.appliesWhenCountries ||
        assignment.appliesWhenCountries.length === 0) &&
      (!assignment.excludesCountries ||
        assignment.excludesCountries.length === 0)
    ) {
      return true;
    }

    // Check if any country in context matches inclusion criteria
    if (
      assignment.appliesWhenCountries &&
      assignment.appliesWhenCountries.length > 0
    ) {
      const hasMatchingCountry = Object.values(countryContext).some(
        (country) =>
          assignment.appliesWhenCountries?.includes(country) ?? false,
      );
      if (!hasMatchingCountry) return false;
    }

    // Check if any country in context matches exclusion criteria
    if (
      assignment.excludesCountries &&
      assignment.excludesCountries.length > 0
    ) {
      const hasExcludedCountry = Object.values(countryContext).some(
        (country) => assignment.excludesCountries?.includes(country) ?? false,
      );
      if (hasExcludedCountry) return false;
    }

    return true;
  }
}

export const countryValidationManager = new CountryValidationManager();
